import { useState, type ReactNode } from "react";
import { readPanelOpen, writePanelOpen } from "../lib/panel-state";

export function Page({ kicker, title, lede, actions, children }: { kicker?: string; title: string; lede?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="page py-5">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          {kicker && <div className="kicker">{kicker}</div>}
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {lede && <p className="mt-0.5 text-sm text-muted-foreground">{lede}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

/** A panel folds when it carries an `id` and `collapsible`; the fold is remembered per browser (Jacky, 4 October
 *  2026: "this job page is overloaded, takes forever to scroll"). `summary` is the one line shown while folded, so a
 *  folded panel still answers its question. Panels without `collapsible` behave exactly as before. */
export function Panel({ id, title, meta, summary, collapsible = false, defaultOpen = true, children, className = "" }: { id?: string; title?: ReactNode; meta?: ReactNode; summary?: ReactNode; collapsible?: boolean; defaultOpen?: boolean; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(() => (collapsible && id ? readPanelOpen(id, defaultOpen) : true));
  const toggle = () => { const next = !open; setOpen(next); if (id) writePanelOpen(id, next); };
  return (
    <section id={id} className={`rounded-lg border bg-card scroll-mt-24 ${className}`}>
      {(title || meta) && (
        <header className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
          {collapsible
            ? <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open} onClick={toggle}>
                <span aria-hidden className={`inline-block w-3 text-micro text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
                <h2 className="font-display text-sm font-semibold">{title}</h2>
                {!open && summary && <span className="min-w-0 truncate text-micro text-muted-foreground">· {summary}</span>}
              </button>
            : <h2 className="font-display text-sm font-semibold">{title}</h2>}
          {meta && <div className="text-micro text-muted-foreground">{meta}</div>}
        </header>
      )}
      {open && <div className="p-4">{children}</div>}
    </section>
  );
}

export function Tile({ n, label, sub, tone = "" }: { n: ReactNode; label: string; sub?: string; tone?: string }) {
  return (
    <div className={`rounded-lg border bg-card px-4 py-3 ${tone}`}>
      <div className="num font-display text-2xl font-semibold">{n}</div>
      <div className="text-sm font-medium">{label}</div>
      {sub && <div className="text-micro text-muted-foreground">{sub}</div>}
    </div>
  );
}
